import { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { QueryClient, QueryClientProvider, onlineManager } from '@tanstack/react-query';
import { ToastProvider } from '@heroui/toast';
import { cn, HeroUIProvider } from '@heroui/react';
import { HelmetProvider, Helmet } from 'react-helmet-async';
import { AuthProvider } from './contexts/auth-context';
import { LocationProvider } from './contexts/location-context';
import { OrganizationProvider } from './contexts/organization-context';
import { ThemeProvider } from './contexts/theme-context';
import GoogleAnalytics from './components/GoogleAnalytics';
import ErrorBoundary from './components/ErrorBoundary';
import ScrollToTop from './components/ScrollToTop';
import { reportError } from './utils/errorReporting';

// Helper function to get site name from environment
const getSiteName = () => {
    return (import.meta as any).env?.VITE_SITE_NAME || 'EventUp';
};

// Import all screens
import Home from './screens/Home';
import AdminPanel from './screens/AdminPanel';
import ImpersonationBanner from './components/admin/ImpersonationBanner';
import Login from './screens/Login';
import Signup from './screens/Signup';
import Profile from './screens/Profile';
import CreateOrganization from './screens/CreateOrganization';
import EventDetails from './screens/EventDetails';
import Search from './screens/Search';
import Cart from './screens/Cart';
import Order from './screens/Order';
import OrderFailed from './screens/OrderFailed';
import Organizer from './screens/Organizer';
import Payouts from './screens/Payouts';
import PromoteEvent from './screens/PromoteEvent';
import PromotedEvent from './screens/PromotedEvent';
import Help from './screens/Help';
import Contact from './screens/Contact';
import ForgotPassword from './screens/ForgotPassword';
import PrivacyPolicy from './screens/PrivacyPolicy';
import TermsConditions from './screens/TermsConditions';
import CookiePolicy from './screens/CookiePolicy';
import UserProfile from './screens/UserProfile';
import ConnectionIssue from './screens/ConnectionIssue';
import NotFound from './screens/NotFound';
import ServerError from './screens/ServerError';
import SeatPlan from './screens/SeatPlan';
import SeatMapEditorScreen from './screens/SeatMapEditorScreen';
import VenueManagementScreen from './screens/VenueManagementScreen';
import Aboutus from './screens/Aboutus';
import HomeIframe from './screens/Iframes';
import TestIframe from './screens/TestIframe';
import Iframes from './screens/Iframes';
import FormScreen from './screens/FormScreen';
import ContactFormScreen from './screens/ContactFormScreen';
import MyOrganizations from './screens/MyOrganizations';

// Create Event now lives under the Organizations page; redirect old bookmarks/links there.
function CreateEventRedirect() {
    const location = useLocation();
    return <Navigate to={`/organizations/events/create${location.search}`} replace />;
}

function App() {
    const [queryClient] = useState(() => new QueryClient({
        defaultOptions: {
            mutations: {
                throwOnError: (error: Error) => {
                    if ((error as any)?.status === 404) {
                        // Handle 404 errors
                    }
                    else if ((error as any)?.status === 500) {
                        //window.location.href = '/500';
                    }
                    return false;
                },
                retry: 1
            },
            queries: {
                refetchOnMount: true,
                refetchOnWindowFocus: false,
                retry: 1,
                throwOnError: (error: Error) => {
                    if ((error as any)?.status === 404) {
                        // Handle 404 errors
                    }
                    else if ((error as any)?.status === 500) {
                        //window.location.href = '/500';
                    }
                    return false;
                }
            },
        }
    }));

    useEffect(() => {
        console.log('onlineManager.isOnline()', onlineManager.isOnline());
        if (onlineManager.isOnline()) {
            if (window.location.pathname.includes('/connectionIssue')) {
                window.location.href = '/';
            }
        }
        else {
            if (!window.location.pathname.includes('/connectionIssue')) {
                window.location.href = '/connectionIssue';
            }
        }
    }, [onlineManager]);

    return (
        <div>
            <ThemeProvider>
            <Router>
                <QueryClientProvider client={queryClient}>
                    <HeroUIProvider>
                        <ToastProvider placement="top-right" regionProps={{ classNames: { base: cn("z-[99999]") } }}
                            toastOffset={16} maxVisibleToasts={1} />
                        <AuthProvider>
                            <LocationProvider>
                                <OrganizationProvider>
                                    <GoogleAnalytics />
                                    <ScrollToTop />
                                    <ImpersonationBanner />
                                    <ErrorBoundary
                                        onError={(error, errorInfo) => {
                                            // Log error to console in development
                                            if (process.env.NODE_ENV === 'development') {
                                                console.error('Application Error:', error, errorInfo);
                                            }
                                            // Report error to centralized error reporting system
                                            reportError(error, errorInfo);
                                        }}
                                    >
                                        <Routes>
                                            <Route path="/" element={<Home iframe={false} />} />
                                            <Route path="/iframe/:page/:id" element={<Iframes />} />
                                            <Route path="/testiframe" element={<TestIframe />} />
                                            <Route path="/seat-plan/:eventId" element={<SeatPlan />} />
                                            <Route path="/seat-map-editor" element={<SeatMapEditorScreen />} />
                                            <Route path="/venue-management" element={<VenueManagementScreen />} />
                                            <Route path="/admin" element={<AdminPanel />} />
                                            <Route path="/login" element={<Login />} />
                                            <Route path="/signup" element={<Signup />} />
                                            <Route path="/organizations/:tab?/:eid?/:activeTab?" element={<MyOrganizations />} />
                                            <Route path="/profile/:tab?/:eid?/:activeTab?" element={<Profile />} />
                                            <Route path="/create-event" element={<CreateEventRedirect />} />
                                            <Route path="/create-organization" element={<CreateOrganization />} />
                                            <Route path="/event/:id" element={<EventDetails />} />
                                            <Route path="/e/:handle" element={<EventDetails />} />
                                            <Route path="/search" element={<Search />} />
                                            <Route path="/checkout" element={<Cart />} />
                                            <Route path="/order" element={<Order />} />
                                            <Route path="/order/failed/:sessionId" element={<OrderFailed />} />
                                            <Route path="/organization/:id" element={<Organizer />} />
                                            <Route path="/payouts" element={<Payouts />} />
                                            <Route path="/promote-event" element={<PromoteEvent />} />
                                            <Route path="/promoted-event/:id" element={<PromotedEvent />} />
                                            <Route path="/help" element={<Help />} />
                                            <Route path="/contact" element={<Contact />} />
                                            <Route path="/aboutus" element={<Aboutus />} />
                                            <Route path="/forgot-password" element={<ForgotPassword />} />
                                            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                                            <Route path="/terms-conditions" element={<TermsConditions />} />
                                            <Route path="/cookie-policy" element={<CookiePolicy />} />
                                            <Route path="/user/:id" element={<UserProfile />} />
                                            <Route path="/form/:formId" element={<FormScreen />} />
                                            <Route path="/contact-form/:listId" element={<ContactFormScreen />} />
                                            <Route path="/connectionIssue" element={<ConnectionIssue />} />
                                            <Route path="/404" element={<NotFound />} />
                                            <Route path="/500" element={<ServerError />} />
                                            <Route path="*" element={<NotFound />} />
                                        </Routes>
                                    </ErrorBoundary>
                                </OrganizationProvider>
                            </LocationProvider>
                        </AuthProvider>


                    </HeroUIProvider>
                </QueryClientProvider>
            </Router>
            </ThemeProvider>
        </div>
    );
}

export default App;
